import React, { useEffect, useState } from "react";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import ModalWebContentForm from "../webcontent/ModalWebContentForm";
import { Item, ItemTypes } from "../api/Items";
import { ReportSummaryItemApi } from "../api/Reports";
import { useFetchItemsForReview } from "../api/ItemsApi";
import { useReportDetail } from "../reports/ReportDetailProvider";
import AddPost, { OnCloseParams } from "../posts/AddPost";
import ModalAffiliationAdd from "../affiliation/ModalAffiliationAdd";
import {
  NewspaperIcon,
  PlusIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { ITEM_SOURCE_AFFILIATION } from "../affiliation/helpers";

type AuditQAModalAddProps = {
  handleAction: (action: "add" | "cancel", value: Item | null) => void;
  behaviors: string[];
  report: ReportSummaryItemApi;
};

function AuditQAModalAdd({
  handleAction,
  behaviors,
  report,
}: AuditQAModalAddProps) {
  const { profileList, blueprint } = useReportDetail();
  const [showModal, setShowModal] = useState(false);
  const [webProfileUUID, setWebProfileUUID] = useState<string | null>(null);
  const {
    isLoading: isLoadingWebContentProfile,
    mutateAsync: loadWebContentProfile,
  } = useFetchItemsForReview();

  useEffect(() => {
    async function fetchProfileUUID() {
      const profile = await loadWebContentProfile({
        report_uuid: report.report_uuid,
        item_type_name: [ItemTypes.WEB_CONTENT_PROFILE],
        page_num: 1,
        page_size: 1,
      });
      if (profile?.items.length) {
        setWebProfileUUID(profile?.items[0].item.external_id ?? null);
      }
    }
    fetchProfileUUID();
  }, [loadWebContentProfile, profileList, report.report_uuid]);

  function close() {
    setShowModal(false);
  }
  const isAffiliation = blueprint?.item_sources?.find(
    (source) => source.name === ITEM_SOURCE_AFFILIATION
  );
  if (
    (!profileList || profileList?.length === 0) &&
    !webProfileUUID &&
    !isAffiliation
  )
    return null;
  if (!webProfileUUID && !isAffiliation) {
    return (
      <div>
        <Button
          text={
            <div className="flex gap-2 items-center">
              <PlusIcon className="w-4 h-4" />
              <>Add Item</>
            </div>
          }
          colorType={ButtonColorTypes.cancel}
          onClick={() => setShowModal(true)}
        />
        <Modal open={showModal} onClose={close} width={"w-full max-w-xl p-3"}>
          <AddPost
            report={report}
            onClose={(item?: OnCloseParams) => {
              if (item) {
                handleAction("add", item.post as unknown as Item);
              }

              setShowModal(false);
            }}
          />
        </Modal>
      </div>
    );
  }
  return (
    <div>
      <Button
        text={
          <div className="flex gap-2 items-center">
            <PlusIcon className="w-4 h-4" />
            <>Add Item</>
          </div>
        }
        colorType={ButtonColorTypes.cancel}
        onClick={() => setShowModal(true)}
      />
      <Modal open={showModal} onClose={close} width={"w-full max-w-lg"}>
        <div className="text-lg font-semibold text-center pb-4">
          What type of item do you want to add?
        </div>
        <div className="flex justify-center items-center gap-2">
          <div>
            <ButtonPostAddNew
              handleAction={(action, value) => {
                handleAction(action, value);
                close();
              }}
              report={report}
            />
          </div>
          <div>
            {!isLoadingWebContentProfile && (
              <ButtonWebcontentAddNew
                handleAction={(action, value) => {
                  handleAction(action, value);
                  close();
                }}
                behaviors={behaviors}
                report={report}
                profileUUID={webProfileUUID ?? ""}
              />
            )}
          </div>
          {isAffiliation && (
            <div>
              <ButtonAffiliationAddNew
                handleAction={(action, value) => {
                  handleAction(action, value);
                  close();
                }}
                behaviors={behaviors}
                report={report}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

type ButtonWebcontentAddNewProps = {
  handleAction: (action: "add" | "cancel", value: Item) => void;
  behaviors: string[];
  report: ReportSummaryItemApi;
  profileUUID: string;
};

const ButtonWebcontentAddNew = ({
  handleAction,
  behaviors,
  report,
  profileUUID,
}: ButtonWebcontentAddNewProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div>
      <div
        onClick={() => {
          setShowModal(true);
        }}
        className="flex gap-2 border border-gray-200 px-4 py-2 rounded-lg text-gray-700 items-center justify-center cursor-pointer text-xs w-28"
      >
        <NewspaperIcon className="w-4 h-4" />
        <>Article</>
      </div>

      <ModalWebContentForm
        showModal={showModal}
        close={(item?: Item) => {
          if (item) {
            handleAction("add", item);
          }
          setShowModal(false);
        }}
        behaviors={behaviors}
        initialState={{ report, profileUUID }}
        isNew={true}
      />
    </div>
  );
};

type ButtonAffiliationAddNewProps = {
  handleAction: (action: "add" | "cancel", value: Item) => void;
  behaviors: string[];
  report: ReportSummaryItemApi;
};

const ButtonAffiliationAddNew = ({
  handleAction,
  behaviors,
  report,
}: ButtonAffiliationAddNewProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div>
      <div
        onClick={() => {
          setShowModal(true);
        }}
        className="flex gap-2 border border-gray-200 px-4 py-2 rounded-lg text-gray-700 items-center justify-center cursor-pointer text-xs w-28"
      >
        <UserGroupIcon className="w-4 h-4" />
        <>Affiliation</>
      </div>

      <ModalAffiliationAdd
        showModal={showModal}
        close={(item?: Item) => {
          if (item) {
            handleAction("add", item);
          }
          setShowModal(false);
        }}
        behaviors={behaviors}
        initialState={{ report }}
        isNew={true}
      />
    </div>
  );
};

type ButtonPostAddNewProps = {
  handleAction: (action: "add" | "cancel", value: Item | null) => void;
  report: ReportSummaryItemApi;
  modalOpen?: boolean;
};

const ButtonPostAddNew = ({
  handleAction,
  report,
  modalOpen = false,
}: ButtonPostAddNewProps) => {
  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    setShowModal(modalOpen);
  }, [modalOpen]);
  return (
    <div>
      {!modalOpen && (
        <div
          onClick={() => {
            setShowModal(true);
          }}
          className="flex gap-2 border border-gray-200 px-4 py-2 rounded-lg text-gray-700 items-center justify-center cursor-pointer text-xs w-28"
        >
          <NewspaperIcon className="w-4 h-4" />
          <>Post</>
        </div>
      )}
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          handleAction("cancel", null);
        }}
        width={"w-full max-w-xl p-3"}
      >
        <AddPost
          report={report}
          onClose={(item?: OnCloseParams) => {
            if (item) {
              handleAction("add", item.post as unknown as Item);
            }
            setShowModal(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default AuditQAModalAdd;
